import type { CardTemplateFieldDocument } from "./product";
import type { FileViewMode, LayoutComponent, LayoutComponentType } from "./templates";

export type LayoutRecipeFamily = "character" | "location" | "item" | "quest" | "creature" | "scene" | "generic";

const mechanicsKeys = /(^|_)(hp|health|max_health|max_hp|resource|energy|stamina|stress|armor|defense|ac|damage|range|charges?|uses|attributes|skills|conditions|initiative|actions?|move|speed|level)($|_)/i;
const fieldKey = (field: CardTemplateFieldDocument) => field.key.toLowerCase();
const title = (field: CardTemplateFieldDocument) => field.label || field.key.replaceAll("_", " ");
const normalizedDesignation = (value: string) => value.trim().toLowerCase().replaceAll("-", "_");

export function inferLayoutRecipeFamily(designation: string): LayoutRecipeFamily {
  const value = normalizedDesignation(designation);
  if (/character|npc|persona|player/.test(value)) return "character";
  if (/location|place|region|world|establishment|area|map/.test(value)) return "location";
  if (/quest|mission|objective|adventure/.test(value)) return "quest";
  if (/creature|monster|enemy|beast/.test(value)) return "creature";
  if (/item|equipment|weapon|armor|tool|consumable|artifact/.test(value)) return "item";
  if (/scene|event|encounter|chapter|session/.test(value)) return "scene";
  return "generic";
}

export function inferMechanicalLayout(fields: readonly CardTemplateFieldDocument[]): boolean {
  return fields.some((field) => mechanicsKeys.test(field.key));
}

const component = (field: CardTemplateFieldDocument, type?: LayoutComponentType, props?: Record<string, unknown>): LayoutComponent => ({
  id: `view-${field.key}`,
  type: type ?? (field.type === "long_text" ? "markdown" : field.type === "number" || field.type === "level" ? "stat_grid" : field.type === "boolean" ? "toggle" : field.type === "reference" ? "reference_grid" : field.type === "multi_select" ? "badge_list" : field.type === "stat_group" ? "stat_grid" : "text"),
  title: title(field),
  binding: `fields.${field.key}`,
  ...(props ? { props: props as NonNullable<LayoutComponent["props"]> } : {}),
});

const find = (fields: readonly CardTemplateFieldDocument[], patterns: RegExp[]): CardTemplateFieldDocument | undefined => fields.find((field) => patterns.some((pattern) => pattern.test(fieldKey(field))));
const take = (fields: readonly CardTemplateFieldDocument[], patterns: RegExp[]): CardTemplateFieldDocument[] => fields.filter((field) => patterns.some((pattern) => pattern.test(fieldKey(field))));
const without = (fields: readonly CardTemplateFieldDocument[], used: readonly (CardTemplateFieldDocument | undefined)[]) => fields.filter((field) => !used.some((candidate) => candidate?.key === field.key));
const unique = (fields: readonly (CardTemplateFieldDocument | undefined)[]) => fields.filter((field, index): field is CardTemplateFieldDocument => Boolean(field) && fields.findIndex((candidate) => candidate?.key === field?.key) === index);
const card = (id: string, cardTitle: string, children: LayoutComponent[], props?: Record<string, unknown>): LayoutComponent => ({ id, type: "card", title: cardTitle, children, ...(props ? { props: props as NonNullable<LayoutComponent["props"]> } : {}) });
const grid = (id: string, children: LayoutComponent[], desktop = 2, tablet = Math.min(2, desktop), phone = 1): LayoutComponent => ({
  id, type: "grid", columns: desktop, props: { columns: desktop, gap: "md" }, responsive: { phone: { columns: phone }, tablet: { columns: tablet }, desktop: { columns: desktop } }, children,
});
const stack = (id: string, children: LayoutComponent[]): LayoutComponent => ({ id, type: "stack", props: { direction: "vertical", gap: "md", align: "stretch" }, children });
const hero = (ratio: "video" | "portrait" | "ultrawide" = "video"): LayoutComponent => ({ id: "file-cover", type: "image_header", binding: "fields.image", props: { aspectRatio: ratio, overlay: "gradient", focalPosition: "center", minHeight: ratio === "ultrawide" ? 180 : 260 } });

function proseSections(fields: readonly CardTemplateFieldDocument[], prefix: string): LayoutComponent[] {
  return fields.map((field) => component(field, field.type === "multi_select" ? "badge_list" : field.type === "reference" ? "reference_grid" : field.type === "boolean" ? "toggle" : "markdown", field.type === "reference" ? { columns: 3, imageAspectRatio: "video" } : undefined)).map((node) => card(`${prefix}-${node.id}`, node.title || "Details", [node], { rounded: "xl" }));
}

function compactLayout(fields: readonly CardTemplateFieldDocument[], family: LayoutRecipeFamily): LayoutComponent[] {
  const short = fields.filter((field) => field.type !== "long_text").slice(0, family === "quest" ? 4 : 3);
  const teaser = fields.find((field) => field.type === "long_text");
  return [stack("compact-root", [
    hero(family === "scene" || family === "quest" ? "ultrawide" : "video"),
    ...(short.length ? [grid("compact-facts", short.map((field) => component(field, field.type === "number" || field.type === "level" ? "stat_grid" : "field_list")), Math.min(3, short.length), 2, 1)] : []),
    ...(teaser ? [component(teaser, "text", { variant: "muted" })] : []),
  ])];
}

function mentionLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const status = find(fields, [/status/, /role/, /kind/, /type/, /region/, /rarity/]);
  return [stack("mention-root", [
    { id: "mention-title", type: "text", binding: "title", props: { variant: "lead" }, styleToken: "accent" },
    ...(status ? [component(status, "badge", { variant: "outline" })] : []),
  ])];
}

function characterLayout(fields: readonly CardTemplateFieldDocument[], mechanical: boolean): LayoutComponent[] {
  const role = find(fields, [/^role$/, /class/, /archetype/, /occupation/, /kind/, /species/]);
  const motive = find(fields, [/motivation/, /goal/, /desire/, /drive/, /objective/]);
  const health = find(fields, [/^health$/, /^hp$/, /current_health/, /hit_points/]);
  const maxHealth = find(fields, [/max_health/, /health_max/, /max_hp/, /hp_max/]);
  const resource = find(fields, [/resource/, /energy/, /stamina/, /mana/, /stress/, /resolve/, /grit/]);
  const attributes = find(fields, [/attributes?/, /stats?/, /abilities/]);
  const conditions = find(fields, [/conditions?/, /statuses/]);
  const relationships = take(fields, [/relationship/, /bond/, /ally/, /rival/, /companion/]);
  const secrets = take(fields, [/secret/, /hidden/, /gm_/]);
  const identity = unique([role, motive]);
  const statusNodes: LayoutComponent[] = [];
  if (health) statusNodes.push(component(health, "progress", { ...(maxHealth ? { maxBinding: `fields.${maxHealth.key}` } : { max: 100 }), showValue: true, stepper: true, color: "#ef476f" }));
  if (resource) statusNodes.push(component(resource, "dots", { max: resource.max ?? 5, showValue: true }));
  if (attributes) statusNodes.push(component(attributes, "stat_grid", { columns: 4, size: "md" }));
  if (conditions) statusNodes.push(component(conditions, "condition_toggles", { size: "md" }));
  const used = unique([role, motive, health, maxHealth, resource, attributes, conditions, ...relationships, ...secrets]);
  const details = without(fields, used);
  const tabs: LayoutComponent[] = [];
  if (details.length) tabs.push(card("character-overview", "ภาพรวม", proseSections(details, "character-detail")));
  if (relationships.length) tabs.push(card("character-relationships", "ความสัมพันธ์", relationships.map((field) => component(field, field.type === "reference" ? "relationship_list" : "markdown", { columns: 2 }))));
  if (secrets.length) tabs.push(card("character-secrets", "ข้อมูล GM", secrets.map((field) => component(field, "callout", { variant: "panel", icon: "✦" })), { borderColor: "var(--pf-accent)" }));
  return [stack("character-root", [
    hero("video"),
    ...(identity.length ? [grid("character-identity", identity.map((field) => component(field, "field_list")), identity.length, Math.min(2, identity.length), 1)] : []),
    ...(mechanical && statusNodes.length ? [card("character-live-state", "สถานะปัจจุบัน", statusNodes, { rounded: "xl" })] : []),
    ...(tabs.length > 1 ? [{ id: "character-tabs", type: "tabs", props: { variant: "pill", orientation: "horizontal" }, children: tabs } satisfies LayoutComponent] : tabs),
  ])];
}

function locationLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const map = find(fields, [/map/, /coordinates?/, /anchors?/]);
  const facts = take(fields, [/region/, /terrain/, /climate/, /district/, /type/, /status/, /hours/, /price/, /legality/, /lifestyle/]).slice(0, 6);
  const links = take(fields, [/people/, /residents?/, /factions?/, /routes?/, /connections?/, /nearby/]);
  const used = unique([map, ...facts, ...links]);
  const description = without(fields, used);
  const tabs: LayoutComponent[] = [];
  if (description.length) tabs.push(card("location-overview", "ภาพรวม", proseSections(description, "location-detail")));
  if (links.length) tabs.push(card("location-connections", "ผู้คนและเส้นทาง", links.map((field) => component(field, field.type === "reference" ? "reference_grid" : "markdown", { columns: 3, imageAspectRatio: "video" }))));
  return [stack("location-root", [hero("ultrawide"), ...(facts.length ? [grid("location-facts", facts.map((field) => component(field, "field_list")), Math.min(4, facts.length), 2, 1)] : []), ...(map ? [component(map, "map_preview", { aspectRatio: "video", showLabels: true, minHeight: 360 })] : []), ...(tabs.length > 1 ? [{ id: "location-tabs", type: "tabs", props: { variant: "line" }, children: tabs } satisfies LayoutComponent] : tabs)])];
}

function itemLayout(fields: readonly CardTemplateFieldDocument[], mechanical: boolean): LayoutComponent[] {
  const facts = take(fields, [/kind/, /type/, /category/, /rarity/, /price/, /value/, /weight/, /quantity/, /slot/]).slice(0, 8);
  const mechanics = mechanical ? take(fields, [/damage/, /defense/, /range/, /effect/, /properties/, /uses/, /charges/]) : [];
  const used = unique([...facts, ...mechanics]);
  const details = without(fields, used);
  return [stack("item-root", [hero("video"), ...(facts.length ? [grid("item-facts", facts.map((field) => component(field, field.type === "number" ? "stat_grid" : "field_list")), Math.min(4, facts.length), 2, 1)] : []), ...(mechanics.length ? [card("item-mechanics", "คุณสมบัติ", [grid("item-mechanics-grid", mechanics.map((field) => component(field, field.type === "number" ? "stat_grid" : field.type === "multi_select" ? "badge_list" : "field_list")), Math.min(3, mechanics.length), 2, 1)])] : []), ...proseSections(details, "item-detail")])];
}

function questLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const progress = find(fields, [/progress/, /completion/, /percent/]);
  const status = find(fields, [/status/, /state/, /phase/]);
  const giver = find(fields, [/giver/, /owner/, /contact/]);
  const objectives = find(fields, [/objectives?/, /tasks?/, /steps?/]);
  const rewards = take(fields, [/rewards?/, /payment/, /loot/]);
  const used = unique([progress, status, giver, objectives, ...rewards]);
  const details = without(fields, used);
  const tabs = [
    ...(objectives ? [card("quest-objectives", "เป้าหมาย", [component(objectives, objectives.type === "multi_select" ? "checklist" : "markdown")])] : []),
    ...(details.length ? [card("quest-details", "รายละเอียด", proseSections(details, "quest-detail"))] : []),
    ...(rewards.length ? [card("quest-rewards", "รางวัล", rewards.map((field) => component(field, field.type === "reference" ? "reference_grid" : "markdown")))] : []),
  ];
  return [stack("quest-root", [hero("ultrawide"), ...((status || giver) ? [grid("quest-meta", unique([status, giver]).map((field) => component(field, "badge")), 2, 2, 1)] : []), ...(progress ? [component(progress, "progress", { max: progress.max ?? 100, showValue: true, stepper: false })] : []), ...(tabs.length > 1 ? [{ id: "quest-tabs", type: "tabs", props: { variant: "pill" }, children: tabs } satisfies LayoutComponent] : tabs)])];
}

function creatureLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const stats = take(fields, [/health/, /hp/, /defense/, /armor/, /threat/, /speed/, /move/, /initiative/, /actions?/]).slice(0, 8);
  const health = find(stats, [/^health$/, /^hp$/, /current_health/]);
  const maxHealth = find(fields, [/max_health/, /health_max/, /max_hp/]);
  const remainingStats = stats.filter((field) => field.key !== health?.key && field.key !== maxHealth?.key);
  const used = unique([...stats, maxHealth]);
  const details = without(fields, used);
  return [stack("creature-root", [hero("video"), ...(health ? [component(health, "progress", { ...(maxHealth ? { maxBinding: `fields.${maxHealth.key}` } : { max: 100 }), showValue: true, color: "#ef476f" })] : []), ...(remainingStats.length ? [grid("creature-stats", remainingStats.map((field) => component(field, field.type === "number" ? "stat_grid" : "field_list")), Math.min(4, remainingStats.length), 2, 1)] : []), ...proseSections(details, "creature-detail")])];
}

function sceneLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const meta = take(fields, [/status/, /mood/, /time/, /date/, /location/, /pressure/, /phase/]).slice(0, 6);
  const used = unique(meta);
  const details = without(fields, used);
  return [stack("scene-root", [hero("ultrawide"), ...(meta.length ? [grid("scene-meta", meta.map((field) => component(field, /date|time/.test(field.key) ? "formatted_date" : "badge")), Math.min(4, meta.length), 2, 1)] : []), ...proseSections(details, "scene-detail")])];
}

function genericLayout(fields: readonly CardTemplateFieldDocument[]): LayoutComponent[] {
  const short = fields.filter((field) => field.type !== "long_text" && field.type !== "reference").slice(0, 8);
  const references = fields.filter((field) => field.type === "reference");
  const details = fields.filter((field) => !short.includes(field) && !references.includes(field));
  return [stack("generic-root", [hero("video"), ...(short.length ? [grid("generic-facts", short.map((field) => component(field)), Math.min(4, short.length), 2, 1)] : []), ...proseSections(details, "generic-detail"), ...references.map((field) => component(field, "reference_grid", { columns: 3, imageAspectRatio: "video" }))])];
}

export function buildRecommendedFileLayout(designation: string, fields: readonly CardTemplateFieldDocument[], mode: FileViewMode): LayoutComponent[] {
  const family = inferLayoutRecipeFamily(designation);
  if (mode === "mention") return mentionLayout(fields);
  if (mode === "compact") return compactLayout(fields, family);
  const mechanical = inferMechanicalLayout(fields);
  if (family === "character") return characterLayout(fields, mechanical);
  if (family === "location") return locationLayout(fields);
  if (family === "item") return itemLayout(fields, mechanical);
  if (family === "quest") return questLayout(fields);
  if (family === "creature") return creatureLayout(fields);
  if (family === "scene") return sceneLayout(fields);
  return genericLayout(fields);
}

// -----------------------------------------------------------------------------
// STARTER PRESETS (Craft Layout DSL Blueprints for the 5 Primary Archetypes)
// -----------------------------------------------------------------------------

export type PresetArchetype = "character" | "location" | "item" | "creature" | "lore";
export type PresetGenre = "fantasy" | "xianxia" | "cyberpunk" | "investigation" | "general";

export interface StarterPreset {
  id: string;
  name: string;
  description: string;
  archetype: PresetArchetype;
  genre: PresetGenre;
  icon: string;
  fields: import("./templates").FieldDefinition[];
  layout: import("./layouts").LayoutDocument;
  sampleData: Record<string, import("./types").JsonValue>;
}

export interface LayoutFeatureToggle {
  id: string;
  name: string;
  description: string;
  fields: import("./templates").FieldDefinition[];
  elements: Record<string, import("./layouts").LayoutElement>;
  mountTargetParentId: string;
  mountTargetChildId: string;
}


import craftBlueprintsData from "./craft-blueprints-data.json";

export const STARTER_PRESETS: readonly StarterPreset[] = craftBlueprintsData as unknown as StarterPreset[];


export function getStarterPresets(filter?: { archetype?: PresetArchetype; genre?: PresetGenre }): readonly StarterPreset[] {
  return STARTER_PRESETS.filter((preset) => {
    if (filter?.archetype && preset.archetype !== filter.archetype) return false;
    if (filter?.genre && preset.genre !== filter.genre) return false;
    return true;
  });
}

export function getStarterPresetById(id: string): StarterPreset | undefined {
  return STARTER_PRESETS.find((p) => p.id === id);
}
